/** Refresh promo copy and progress from current settings and availability. */
import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

const PROMO_BAR_UPDATER_STATE_KEY = '__mmPromoBarUpdaterState';
const promoBarUpdaterState = (window[PROMO_BAR_UPDATER_STATE_KEY] = window[PROMO_BAR_UPDATER_STATE_KEY] || {
  initialized: false,
  scheduledRefreshTimer: null,
  requests: new WeakMap(),
});

async function refreshPromoBarSection(element, dataAttribute) {
  const sectionEl = element.closest('.shopify-section');
  const sectionId = sectionEl?.id?.replace('shopify-section-', '');
  if (!sectionId) return;

  const version = (promoBarUpdaterState.requests.get(element) || 0) + 1;
  promoBarUpdaterState.requests.set(element, version);
  try {
    // Keep the visitor's locale, selected variant and preview theme. Avoid reusing
    // a browser-cached fragment after the merchant changes a reward.
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) return;
    const html = await response.text();
    if (!element.isConnected || promoBarUpdaterState.requests.get(element) !== version) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const hostName = element.dataset.promoRefresh;
    const freshHost = hostName ? doc.querySelector(`[data-promo-refresh="${hostName}"]`) : null;
    const freshBar = doc.querySelector(`[${dataAttribute}]`);

    if (hostName) {
      // Even a successful HTTP response may be an error document. A legitimate
      // empty offer still includes the stable host from Liquid.
      if (!freshHost && !freshBar) return;
      // The stable host remains when no rewards can be redeemed, allowing a
      // later restock or replacement to appear without reloading the whole page.
      const contents = freshHost ? [...freshHost.childNodes] : (freshBar ? [freshBar] : []);
      element.replaceChildren(...contents);
    } else if (freshBar) {
      element.replaceWith(freshBar);
    } else {
      // Compatibility for older quantity placements without a persistent host.
      const placeholder = document.createElement('span');
      placeholder.setAttribute(dataAttribute, '');
      placeholder.hidden = true;
      element.replaceWith(placeholder);
    }
  } catch (error) {
    console.error('Error refreshing promo bar:', error);
  }
}

async function refreshPromoBars() {
  const placements = [
    ['announcement', 'data-announcement-promo'],
    ['pdp', 'data-pdp-promo-bar'],
    [null, 'data-qty-promo-bar'],
    [null, 'data-qty-promo-pdp'],
  ];
  await Promise.all(placements.flatMap(([host, attribute]) => {
    const hosts = host ? [...document.querySelectorAll(`[data-promo-refresh="${host}"]`)] : [];
    const elements = hosts.length ? hosts : [...document.querySelectorAll(`[${attribute}]`)];
    return elements.map(element => refreshPromoBarSection(element, attribute));
  }));
}

function schedulePromoBarRefresh() {
  window.clearTimeout(promoBarUpdaterState.scheduledRefreshTimer);
  promoBarUpdaterState.scheduledRefreshTimer = window.setTimeout(() => {
    promoBarUpdaterState.scheduledRefreshTimer = null;
    refreshPromoBars();
  }, 0);
}

if (!promoBarUpdaterState.initialized) {
  // Cart updates may morph these hosts, so wait until the next task to read them.
  document.addEventListener(ThemeEvents.cartUpdate, schedulePromoBarRefresh, { capture: true });
  document.addEventListener(ThemeEvents.discountUpdate, schedulePromoBarRefresh, { capture: true });
  document.addEventListener(DialogOpenEvent.eventName, (event) => {
    if (event.target.matches?.('cart-drawer-component')) schedulePromoBarRefresh();
  }, true);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') schedulePromoBarRefresh();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedulePromoBarRefresh();
  });
  promoBarUpdaterState.initialized = true;
}

export { refreshPromoBars };
